import React, { useState } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import { EditorDemoStore } from '../EditorDemoStore';
import { useDemoStore } from '../EditorDemo';

// Placeholder photos for demo purposes
const PLACEHOLDER_PHOTOS = [
  'https://placehold.co/800x450/333/FFF?text=Photo+1',
  'https://placehold.co/800x450/334433/FFF?text=Photo+2',
  'https://placehold.co/800x450/443333/FFF?text=Photo+3',
  'https://placehold.co/800x450/334466/FFF?text=Photo+4',
  'https://placehold.co/800x450/664433/FFF?text=Photo+5',
  'https://placehold.co/800x450/553344/FFF?text=Photo+6',
];

interface PhotoSelectionModalProps {
  // No longer need demoStore prop
}

/**
 * A modal component for selecting photos to attach to transcript
 */
const PhotoSelectionModal: React.FC<PhotoSelectionModalProps> = observer(
  () => {
    const demoStore = useDemoStore();
    const [selectedPhotoUrl, setSelectedPhotoUrl] = useState<string | null>(null);

    const handleSubmit = () => {
      if (selectedPhotoUrl && demoStore.selectedWordIds.length > 0) {
        demoStore.attachPhotoToTranscript(selectedPhotoUrl);
        demoStore.closePhotoSelectionModal();
      }
    };

    const handleCancel = () => {
      demoStore.closePhotoSelectionModal();
    };

    // Don't render if modal is not open
    if (!demoStore.isPhotoSelectionModalOpen) return null;

    return createPortal(
      <ModalOverlay>
        <ModalContainer>
          <CloseButton onClick={handleCancel}>✕</CloseButton>
          <ModalHeader>
            <ModalTitle>Select Photo to Attach</ModalTitle>
            <ModalSubtitle>
              Selected Words: {demoStore.selectedWordIds.length}
            </ModalSubtitle>
          </ModalHeader>
          <ModalContent>
            <PhotoGrid>
              {PLACEHOLDER_PHOTOS.map((photoUrl, index) => (
                <PhotoItem
                  key={index}
                  isSelected={photoUrl === selectedPhotoUrl}
                  onClick={() => setSelectedPhotoUrl(photoUrl)}
                >
                  <PhotoImage src={photoUrl} alt={`Placeholder ${index + 1}`} />
                  {photoUrl === selectedPhotoUrl && <SelectedIndicator />}
                </PhotoItem>
              ))}
            </PhotoGrid>
          </ModalContent>
          <ModalFooter>
            <CancelButton onClick={handleCancel}>Cancel</CancelButton>
            <AttachButton
              onClick={handleSubmit}
              disabled={!selectedPhotoUrl}
            >
              Attach Photo
            </AttachButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>,
      document.body
    );
  }
);

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: #171724;
  border-radius: 8px;
  width: 600px;
  max-width: 90vw;
  padding: 24px;
  color: #ffffff;
  position: relative;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 18px;
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  &:hover {
    color: #fff;
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 20px;
  text-align: center;
`;

const ModalTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #45d483;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ModalSubtitle = styled.p`
  margin: 0;
  color: #ccc;
  font-size: 14px;
`;

const ModalContent = styled.div`
  margin-bottom: 24px;
  max-height: 60vh;
  overflow-y: auto;
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  padding: 8px;
`;

const PhotoItem = styled.div<{ isSelected: boolean }>`
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  aspect-ratio: 16/9;
  cursor: pointer;
  border: 2px solid ${(props) => (props.isSelected ? '#45d483' : 'transparent')};
  transition: transform 0.2s, border-color 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const PhotoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const SelectedIndicator = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  background-color: #45d483;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #171724;
  font-weight: bold;
  
  &:after {
    content: '✓';
    font-size: 12px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
`;

const CancelButton = styled(Button)`
  background: transparent;
  border: 1px solid #45d483;
  color: #45d483;
  &:hover {
    background: rgba(69, 212, 131, 0.1);
  }
`;

const AttachButton = styled(Button)<{ disabled: boolean }>`
  background: ${(props) => (props.disabled ? '#2a2a3a' : '#45d483')};
  border: 1px solid ${(props) => (props.disabled ? '#2a2a3a' : '#45d483')};
  color: ${(props) => (props.disabled ? '#666' : '#171724')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  
  &:hover {
    background: ${(props) => (props.disabled ? '#2a2a3a' : '#3bb873')};
  }
`;

export default PhotoSelectionModal;